/**
 * AddBuildingExternalContactModal
 * Pick-or-create modal for linking external contacts to a building.
 * Mirrors AddExternalContactModal (project version); the note is a building note.
 * Only rendered for admins, so the admin-gated registry calls inside
 * ExternalUserSelector / CreateExternalUserForm are never reached by non-admins.
 */
import React, { useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { Plus } from 'lucide-react';
import ExternalUserSelector from '../../../Components/ExternalUserSelector';
import CreateExternalUserForm from '../../../Components/CreateExternalUserForm';
import { ExternalUser } from '../../../types/guestAccess';
import { MAX_BUILDING_NOTE_LENGTH } from '../../../api/buildingExternalContactsApi';

interface AddBuildingExternalContactModalProps {
    show: boolean;
    onHide: () => void;
    onAdd: (externalUserId: string, buildingNote?: string) => Promise<void>;
    submitting: boolean;
    /** UIDs of contacts already linked to this building */
    linkedContactIds: string[];
}

type Step = 'select' | 'create-user';

export const AddBuildingExternalContactModal: React.FC<AddBuildingExternalContactModalProps> = ({
    show,
    onHide,
    onAdd,
    submitting,
    linkedContactIds,
}) => {
    const [step, setStep] = useState<Step>('select');
    const [selectedUser, setSelectedUser] = useState<ExternalUser | null>(null);
    const [buildingNote, setBuildingNote] = useState('');
    const [error, setError] = useState<string | null>(null);

    const resetState = () => {
        setStep('select');
        setSelectedUser(null);
        setBuildingNote('');
        setError(null);
    };

    const handleClose = () => {
        resetState();
        onHide();
    };

    const handleAddContact = async () => {
        if (!selectedUser) {
            setStep('create-user');
            return;
        }

        setError(null);

        try {
            await onAdd(selectedUser.uid, buildingNote.trim() || undefined);
            handleClose();
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Kunde inte lägga till kontakt';
            setError(message);
        }
    };

    const handleUserCreated = (user: ExternalUser) => {
        // The new user is selected directly so it can be added immediately.
        setSelectedUser(user);
        setStep('select');
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    {step === 'select' && 'Lägg till extern kontakt'}
                    {step === 'create-user' && 'Skapa ny extern kontakt'}
                </Modal.Title>
            </Modal.Header>

            {step === 'create-user' ? (
                <CreateExternalUserForm
                    onUserCreated={handleUserCreated}
                    onCancel={() => setStep('select')}
                />
            ) : (
                <>
                    <Modal.Body>
                        {error && <Alert variant="danger">{error}</Alert>}

                        <Form.Group className="mb-3">
                            <Form.Label>Välj extern kontakt</Form.Label>
                            <ExternalUserSelector
                                onSelect={setSelectedUser}
                                onCreateNew={() => setStep('create-user')}
                                selectedUser={selectedUser}
                                disabledIds={linkedContactIds}
                            />
                        </Form.Group>

                        {selectedUser && (
                            <Form.Group className="mb-3">
                                <Form.Label>Byggnadsanteckning (valfri)</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    maxLength={MAX_BUILDING_NOTE_LENGTH}
                                    value={buildingNote}
                                    onChange={(e) => setBuildingNote(e.target.value)}
                                    placeholder="T.ex. 'Ansvarig för fastighetsskötsel'"
                                />
                                <small className={`d-block mt-1 text-end ${buildingNote.length >= MAX_BUILDING_NOTE_LENGTH ? 'text-danger' : 'text-muted'}`}>
                                    {buildingNote.length} / {MAX_BUILDING_NOTE_LENGTH}
                                </small>
                                <Form.Text className="text-muted">
                                    Lägg till en kort notering om varför denna kontakt är relevant för byggnaden.
                                </Form.Text>
                            </Form.Group>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleClose}>
                            Avbryt
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleAddContact}
                            disabled={submitting}
                        >
                            <Plus size={18} className="me-2" />
                            {submitting ? 'Lägger till...' : selectedUser ? 'Lägg till kontakt' : 'Skapa ny kontakt'}
                        </Button>
                    </Modal.Footer>
                </>
            )}
        </Modal>
    );
};
